import type { StateColourOverrideDto } from '../../api/agwa-api-model';
import {
  STATE_PALETTE_COLOURS,
  type StatePaletteColour,
} from './state-colour-registry';

/**
 * The live state colour overrides, shared by every badge on screen.
 *
 * The server stores only DEVIATIONS from the code defaults (state-colour-standard.md); this
 * store holds them once per session and the registry's resolvers consult it, so an admin's
 * change repaints every badge without any component knowing the persistence exists. Loading is
 * lazy - the first badge (or the settings page) triggers one fetch and one SSE subscription -
 * and every badge renders correctly from code defaults while the fetch is in flight.
 *
 * PURE DATA BY DESIGN: no api client, no SSE, no component imports. The registry imports
 * this module and Playwright specs import the registry, so anything heavier here drags the
 * whole app into a node-side test transform. The fetch and the SSE subscription live in
 * state-colour-overrides-loader.ts, which only screen code imports.
 */

export const STATE_COLOURS_TOPIC = 'state-colours';

/** Label overrides by labelKey; palette-validated on ingest. */
const labelOverrides = new Map<string, StatePaletteColour>();
/** Per-use overrides by `${domainKey}\u0000${valueKey}` (a NUL separator, written as the
 *  escape - a raw control byte made this file binary to git), lower-cased for the camelCase wire. */
const useOverrides = new Map<string, StatePaletteColour>();
/** Raw rows, for the settings page (concurrency tokens travel with them). */
let rows: StateColourOverrideDto[] = [];

const listeners = new Set<() => void>();

function useKey(domainKey: string, valueKey: string): string {
  return `${domainKey.toLowerCase()}\u0000${valueKey.toLowerCase()}`;
}

/**
 * Only palette colours pass ingest. A stored colour the palette no longer carries (a key
 * retired after the override was saved) is dropped with a console error rather than rendered:
 * an unknown key would paint NOTHING - the undefined-custom-property trap - which is worse
 * than the code default, and the error keeps the mismatch loud enough to fix.
 */
function toPaletteColour(colour: string): StatePaletteColour | undefined {
  return (STATE_PALETTE_COLOURS as readonly string[]).includes(colour)
    ? (colour as StatePaletteColour)
    : undefined;
}

function ingest(dtoRows: StateColourOverrideDto[]): void {
  labelOverrides.clear();
  useOverrides.clear();
  rows = dtoRows;

  for (const row of dtoRows) {
    const colour = toPaletteColour(row.colour);
    if (!colour) {
      console.error(
        `[state-colours] stored override colour '${row.colour}' is not in the palette; using the code default`
      );
      continue;
    }
    if (row.labelKey) {
      labelOverrides.set(row.labelKey, colour);
    } else if (row.domainKey && row.valueKey) {
      useOverrides.set(useKey(row.domainKey, row.valueKey), colour);
    }
  }

  for (const listener of listeners) listener();
}

/**
 * Replace the store from a write response, so the admin's own save repaints immediately
 * without waiting for the SSE round trip.
 */
export function setStateColourOverrides(
  dtoRows: StateColourOverrideDto[]
): void {
  ingest(dtoRows);
}

export function getLabelOverrideColour(
  labelKey: string
): StatePaletteColour | undefined {
  return labelOverrides.get(labelKey);
}

export function getUseOverrideColour(
  domainKey: string,
  valueKey: string
): StatePaletteColour | undefined {
  return useOverrides.get(useKey(domainKey, valueKey));
}

/** The stored row behind a label override, for its concurrency token. */
export function getLabelOverrideRow(
  labelKey: string
): StateColourOverrideDto | undefined {
  return rows.find((row) => row.labelKey === labelKey);
}

/** The stored row behind a per-use override, matched with the wire's casing tolerance. */
export function getUseOverrideRow(
  domainKey: string,
  valueKey: string
): StateColourOverrideDto | undefined {
  return rows.find(
    (row) =>
      row.domainKey?.toLowerCase() === domainKey.toLowerCase() &&
      row.valueKey?.toLowerCase() === valueKey.toLowerCase()
  );
}

/** Notify on every store change; returns the unsubscribe. Badges re-render through this. */
export function subscribeStateColourOverrides(
  listener: () => void
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
